/**
 * **Item bases** (PRD #795): the content every drop is rolled from.
 *
 * A base is a pure declaration, in the idiom `src/sim/arpg/classes/` and
 * `src/sim/arpgMonsters/` already use: what family it belongs to, which slot it
 * fills, which classes may wear it, what its implicit stat is before any
 * material tier multiplies it, and which existing baked art draws it.
 *
 * **Families are declared once and expanded across slots.** Six armor families
 * across five armour slots is thirty bases, and hand-writing thirty near
 * identical records is thirty chances to typo an implicit. The family declares
 * its class restriction, its armour scalar, its signature trait, and its art;
 * the slot table declares the implicit share; the expansion is what produces
 * the bases. Adding a family is one record.
 *
 * **A family keeps its identity at every tier.** The material ladder multiplies
 * the implicit and nothing else, so a Valorite leather tunic is a better
 * leather tunic rather than a worse plate one. What makes leather leather is
 * the {@link ArpgItemFamily.trait} its chest piece carries, and a trait is
 * deliberately outside the ladder: it is the same at Normal and at Valorite,
 * which is what stops the ladder from flattening the family axis.
 *
 * **Art is what is already baked.** A base names an existing equipment id for
 * the layer it paints and an existing UO static art id for the ground drop.
 * A base whose slot has no baked layer, which today is every glove and every
 * piece of jewellery, paints nothing and equips anyway: the ladder was costed
 * on the hue filters britbank already has, not on an art budget.
 *
 * Every implicit here is **first-pass** and owned by the tuning PRD (#799).
 */

import type { EquipLayer } from '../../content';
import { arpgClass, type ArpgClassId } from '../classes';

/** Every slot an item can fill. A slot holds at most one item. */
export const ARPG_ITEM_SLOTS = [
  'head',
  'chest',
  'arms',
  'hands',
  'legs',
  'feet',
  'mainHand',
  'offHand',
  'neck',
  'ring',
] as const;

export type ArpgItemSlot = (typeof ARPG_ITEM_SLOTS)[number];

/** The five armour slots a family is expanded across. */
export const ARPG_ARMOR_SLOTS: readonly ArpgItemSlot[] = [
  'head',
  'chest',
  'arms',
  'hands',
  'legs',
];

export function isArpgItemSlot(value: unknown): value is ArpgItemSlot {
  return (
    typeof value === 'string' &&
    (ARPG_ITEM_SLOTS as readonly string[]).includes(value)
  );
}

/**
 * What a base's implicit stat is.
 *
 * Armour is percentage points of physical damage turned away, damage is the
 * band every weapon payload scales from, and mana is the pool a casting
 * off-hand deepens. Three kinds is the whole vocabulary, because the implicit
 * is the one number the material ladder multiplies and every extra kind is
 * another thing a tier has to mean.
 */
export type ArpgImplicitKind = 'armor' | 'damage' | 'mana';

/**
 * A family's signature, carried by its chest piece and untouched by the ladder.
 *
 * Small on purpose. It exists to make a family a decision rather than a
 * cosmetic, and it is the same at every tier, which is what keeps the family
 * axis and the material axis independent.
 */
export interface ArpgFamilyTrait {
  attackSpeedPercent?: number;
  castSpeedPercent?: number;
  maxManaFlat?: number;
  maxHealthFlat?: number;
  critChance?: number;
}

/** One item base: what a drop names before anything is rolled onto it. */
export interface ArpgItemBase {
  id: string;
  name: string;
  slot: ArpgItemSlot;
  /** The family this base belongs to, or `jewelry` for the ladderless slots. */
  family: string;
  /** Classes that may equip it. An empty set means every class. */
  classIds: ReadonlySet<ArpgClassId>;
  /** The Hero level this base needs before any item level is considered. */
  minLevel: number;
  implicit: { kind: ArpgImplicitKind; value: number };
  /** Whether the material ladder applies. False for jewellery. */
  ladder: boolean;
  /** The family signature, present only on the piece that carries it. */
  trait?: ArpgFamilyTrait;
  /** The equipment id painted on the paperdoll, when baked art exists. */
  equipmentId?: string;
  /** The render layer the equipment id occupies, for validation. */
  layer?: EquipLayer;
  /** The UO static art id drawn on the ground and in the stash. */
  artId: number;
}

/** One armor family, expanded across the five armour slots. */
interface ArpgItemFamily {
  id: string;
  name: string;
  classIds: readonly ArpgClassId[];
  /** Multiplies the slot's armour share. Cloth is soft; plate is not. */
  armorScalar: number;
  minLevel: number;
  /** Carried by the family's chest piece and never by the ladder. */
  trait: ArpgFamilyTrait;
  /** Per-slot art and, where baked art exists, the layer it paints. */
  pieces: Readonly<
    Partial<
      Record<
        ArpgItemSlot,
        { name: string; artId: number; equipmentId?: string; layer?: EquipLayer }
      >
    >
  >;
}

/**
 * The armour share of each slot, before the family scalar.
 *
 * A chest is worth more than a glove, which is the only thing this table says.
 * The sum across the five armour slots plus footwear is what a full set is
 * worth at Normal, and the material ladder is what multiplies it from there.
 */
const ARMOR_SHARE: Readonly<Record<string, number>> = {
  head: 3,
  chest: 5,
  arms: 3,
  hands: 2,
  legs: 4,
};

const FAMILIES: readonly ArpgItemFamily[] = [
  {
    id: 'cloth',
    name: 'Cloth',
    classIds: ['mage', 'necromancer'],
    armorScalar: 0.6,
    minLevel: 1,
    // The caster's family: it turns the least away and pays for it in pool.
    trait: { maxManaFlat: 10, castSpeedPercent: 2 },
    pieces: {
      head: { name: 'Wizard Hat', artId: 0x1718, equipmentId: 'hat.wizard', layer: 'headgear' },
      chest: { name: 'Robe', artId: 0x1f03, equipmentId: 'robe.plain', layer: 'robe' },
      arms: { name: 'Cloth Sleeves', artId: 0x1517 },
      hands: { name: 'Cloth Wraps', artId: 0x13c6 },
      legs: { name: 'Cloth Leggings', artId: 0x1539, equipmentId: 'pants.long', layer: 'pants' },
    },
  },
  {
    id: 'leather',
    name: 'Leather',
    classIds: ['archer'],
    armorScalar: 0.8,
    minLevel: 1,
    // The fast family. Two percentage points of attack speed at every tier.
    trait: { attackSpeedPercent: 3 },
    pieces: {
      head: { name: 'Leather Cap', artId: 0x1db9, equipmentId: 'hat.cap', layer: 'headgear' },
      chest: { name: 'Leather Tunic', artId: 0x13cc, equipmentId: 'tunic', layer: 'shirt' },
      arms: { name: 'Leather Sleeves', artId: 0x13cd },
      hands: { name: 'Leather Gloves', artId: 0x13c6 },
      legs: { name: 'Leather Leggings', artId: 0x13cb, equipmentId: 'pants.long', layer: 'pants' },
    },
  },
  {
    id: 'studded',
    name: 'Studded',
    classIds: ['archer'],
    armorScalar: 1,
    minLevel: 3,
    // Leather with rivets: it trades a little speed for the first real crit.
    trait: { attackSpeedPercent: 1, critChance: 0.02 },
    pieces: {
      head: { name: 'Studded Gorget', artId: 0x13d6 },
      chest: { name: 'Studded Tunic', artId: 0x13db, equipmentId: 'tunic', layer: 'shirt' },
      arms: { name: 'Studded Sleeves', artId: 0x13dc },
      hands: { name: 'Studded Gloves', artId: 0x13d5 },
      legs: { name: 'Studded Leggings', artId: 0x13da, equipmentId: 'pants.long', layer: 'pants' },
    },
  },
  {
    id: 'ring',
    name: 'Ringmail',
    classIds: ['warrior'],
    armorScalar: 1.15,
    minLevel: 3,
    trait: { maxHealthFlat: 15 },
    pieces: {
      head: { name: 'Ringmail Coif', artId: 0x13bb },
      chest: { name: 'Ringmail Tunic', artId: 0x13ec, equipmentId: 'plate.chest', layer: 'armor-chest' },
      arms: { name: 'Ringmail Sleeves', artId: 0x13ee, equipmentId: 'plate.arms', layer: 'armor-arms' },
      hands: { name: 'Ringmail Gloves', artId: 0x13eb },
      legs: { name: 'Ringmail Leggings', artId: 0x13f0, equipmentId: 'plate.legs', layer: 'armor-legs' },
    },
  },
  {
    id: 'chain',
    name: 'Chainmail',
    classIds: ['warrior'],
    armorScalar: 1.3,
    minLevel: 5,
    trait: { maxHealthFlat: 25 },
    pieces: {
      head: { name: 'Chainmail Coif', artId: 0x13bb },
      chest: { name: 'Chainmail Tunic', artId: 0x13bf, equipmentId: 'plate.chest', layer: 'armor-chest' },
      arms: { name: 'Chainmail Sleeves', artId: 0x13c4, equipmentId: 'plate.arms', layer: 'armor-arms' },
      hands: { name: 'Chainmail Gloves', artId: 0x13eb },
      legs: { name: 'Chainmail Leggings', artId: 0x13be, equipmentId: 'plate.legs', layer: 'armor-legs' },
    },
  },
  {
    id: 'plate',
    name: 'Plate',
    classIds: ['warrior'],
    armorScalar: 1.5,
    minLevel: 8,
    // The heaviest family pays for itself in armour alone, and slows the swing.
    trait: { maxHealthFlat: 30, attackSpeedPercent: -2 },
    pieces: {
      head: { name: 'Plate Helm', artId: 0x1412 },
      chest: { name: 'Plate Chest', artId: 0x1415, equipmentId: 'plate.chest', layer: 'armor-chest' },
      arms: { name: 'Plate Arms', artId: 0x1410, equipmentId: 'plate.arms', layer: 'armor-arms' },
      hands: { name: 'Plate Gauntlets', artId: 0x1414 },
      legs: { name: 'Plate Legs', artId: 0x141a, equipmentId: 'plate.legs', layer: 'armor-legs' },
    },
  },
];

/**
 * Footwear, which is its own three-family axis rather than a sixth armour slot
 * of every family. A Hero's boots are a choice about the boots.
 */
const FOOTWEAR: readonly ArpgItemBase[] = [
  base({
    id: 'sandals',
    name: 'Sandals',
    slot: 'feet',
    family: 'footwear',
    classIds: ['mage', 'necromancer'],
    minLevel: 1,
    implicit: { kind: 'armor', value: 1 },
    trait: { castSpeedPercent: 1 },
    equipmentId: 'sandals.plain',
    layer: 'feet',
    artId: 0x170d,
  }),
  base({
    id: 'shoes',
    name: 'Shoes',
    slot: 'feet',
    family: 'footwear',
    classIds: [],
    minLevel: 1,
    implicit: { kind: 'armor', value: 2 },
    equipmentId: 'shoes.plain',
    layer: 'feet',
    artId: 0x170f,
  }),
  base({
    id: 'boots',
    name: 'Boots',
    slot: 'feet',
    family: 'footwear',
    classIds: ['warrior', 'archer'],
    minLevel: 2,
    implicit: { kind: 'armor', value: 3 },
    trait: { maxHealthFlat: 5 },
    equipmentId: 'boots.plain',
    layer: 'feet',
    artId: 0x170b,
  }),
];

/**
 * Weapons and off-hands.
 *
 * A weapon base names a class weapon family by its equipment id, and its
 * implicit damage is that family's declared band: the class is still the one
 * place a bow's damage is written down, and the item is what multiplies it.
 * The Mage declares no weapon base at all, because its implement is its hands
 * and a class that channels bare-handed has nothing to drop.
 */
const HELD: readonly ArpgItemBase[] = [
  weapon({
    id: 'sword.britannian',
    name: 'Broadsword',
    family: 'blade',
    classId: 'warrior',
    artId: 0x0f5e,
  }),
  weapon({
    id: 'halberd',
    name: 'Halberd',
    family: 'polearm',
    classId: 'warrior',
    artId: 0x143e,
  }),
  weapon({ id: 'bow', name: 'Bow', family: 'bow', classId: 'archer', artId: 0x13b2 }),
  weapon({
    id: 'crossbow',
    name: 'Crossbow',
    family: 'bow',
    classId: 'archer',
    artId: 0x0f50,
  }),
  // Off-hands are per class, and the Archer has none: both of its weapon
  // families are two-handed, and a two-handed weapon excludes an off-hand.
  base({
    id: 'shield.heater',
    name: 'Heater Shield',
    slot: 'offHand',
    family: 'shield',
    classIds: ['warrior'],
    minLevel: 1,
    implicit: { kind: 'armor', value: 4 },
    artId: 0x1b76,
  }),
  base({
    id: 'spellbook',
    name: 'Spellbook',
    slot: 'offHand',
    family: 'tome',
    classIds: ['mage', 'necromancer'],
    minLevel: 1,
    implicit: { kind: 'mana', value: 12 },
    artId: 0x0efa,
  }),
];

/**
 * Jewellery, which sits outside the ladder entirely.
 *
 * No material tier, no implicit, and therefore nothing for a tier to multiply:
 * a ring is worth exactly what it rolled, which is what gives the two
 * ladderless slots an identity of their own.
 */
const JEWELRY: readonly ArpgItemBase[] = [
  base({
    id: 'necklace',
    name: 'Necklace',
    slot: 'neck',
    family: 'jewelry',
    classIds: [],
    minLevel: 1,
    implicit: { kind: 'armor', value: 0 },
    ladder: false,
    artId: 0x1085,
  }),
  base({
    id: 'ring',
    name: 'Ring',
    slot: 'ring',
    family: 'jewelry',
    classIds: [],
    minLevel: 1,
    implicit: { kind: 'armor', value: 0 },
    ladder: false,
    artId: 0x108a,
  }),
];

/**
 * One weapon base, whose implicit damage band is read from the class rather
 * than restated here.
 *
 * The class declaration stays the one place a bow's damage is written down, so
 * a weapon rebalance is still one edit and an item can never quietly disagree
 * with the family it belongs to. The base id **is** the equipment id, which is
 * what lets equip validation check a weapon against the class's own weapon map.
 */
function weapon(declaration: {
  id: string;
  name: string;
  family: string;
  classId: ArpgClassId;
  artId: number;
}): ArpgItemBase {
  const family = arpgClass(declaration.classId)?.weapons[declaration.id];
  if (!family) {
    throw new Error(
      `weapon base '${declaration.id}' names no ${declaration.classId} weapon family`,
    );
  }
  return base({
    id: declaration.id,
    name: declaration.name,
    slot: 'mainHand',
    family: declaration.family,
    classIds: [declaration.classId],
    minLevel: 1,
    implicit: { kind: 'damage', value: family.damage },
    equipmentId: declaration.id,
    layer: 'weapon',
    artId: declaration.artId,
  });
}

function base(
  declaration: Omit<ArpgItemBase, 'classIds' | 'ladder'> & {
    classIds: readonly ArpgClassId[];
    ladder?: boolean;
  },
): ArpgItemBase {
  const { classIds, ladder, ...rest } = declaration;
  return { ...rest, classIds: new Set(classIds), ladder: ladder ?? true };
}

/** Expand one family across the armour slots it declares pieces for. */
function expandFamily(family: ArpgItemFamily): ArpgItemBase[] {
  const bases: ArpgItemBase[] = [];
  for (const slot of ARPG_ARMOR_SLOTS) {
    const piece = family.pieces[slot];
    if (!piece) continue;
    bases.push(
      base({
        id: `${family.id}.${slot}`,
        name: piece.name,
        slot,
        family: family.id,
        classIds: family.classIds,
        minLevel: family.minLevel,
        implicit: {
          kind: 'armor',
          value: Math.max(1, Math.round(ARMOR_SHARE[slot]! * family.armorScalar)),
        },
        // The signature rides the chest piece: one trait per outfit, so a full
        // set of a family is a decision rather than five stacked copies of one.
        ...(slot === 'chest' ? { trait: family.trait } : {}),
        ...(piece.equipmentId !== undefined
          ? { equipmentId: piece.equipmentId }
          : {}),
        ...(piece.layer !== undefined ? { layer: piece.layer } : {}),
        artId: piece.artId,
      }),
    );
  }
  return bases;
}

const ALL_BASES: readonly ArpgItemBase[] = [
  ...FAMILIES.flatMap(expandFamily),
  ...FOOTWEAR,
  ...HELD,
  ...JEWELRY,
];

const BY_ID = new Map<string, ArpgItemBase>(
  ALL_BASES.map((definition) => [definition.id, definition]),
);

/** Every declared base, in declaration order. */
export const ARPG_ITEM_BASES: readonly ArpgItemBase[] = ALL_BASES;

export function arpgItemBase(baseId: string): ArpgItemBase | null {
  return BY_ID.get(baseId) ?? null;
}

export function isArpgItemBaseId(value: unknown): value is string {
  return typeof value === 'string' && BY_ID.has(value);
}

/** Whether this class may equip this base. An empty restriction allows every class. */
export function baseAllowsClass(base: ArpgItemBase, classId: ArpgClassId): boolean {
  return base.classIds.size === 0 || base.classIds.has(classId);
}

/** Every base a class can wear, which is what a loot table draws from. */
export function arpgItemBasesForClass(classId: ArpgClassId): ArpgItemBase[] {
  return ALL_BASES.filter((definition) => baseAllowsClass(definition, classId));
}
